import type React from "react";
import { type Context, useCallback, useEffect, useState } from "react";
import { DISPLAY_DEMO } from "src/constants";

type Settings = { [key: string]: any };

interface UserInfos {
    userId: number;
    organizationId: number;
}

export interface StorageManager {
    setItem: (key: string, value: string) => void;
    getItem: (key: string) => string;
    removeItem: (key: string) => void;
}

export interface SettingsContextValue<T> {
    settings: T;
    clearSettings: () => void;
    saveSettings: (update: T) => void;
    setUserInfos: (userInfos: UserInfos) => void;
}

interface SettingsProviderProps<T extends Settings> {
    Context: Context<SettingsContextValue<T>>;
    storageManager: StorageManager;
    defaultSettings: T;
    children?: React.ReactNode;
}

export function SettingsProvider<T extends Settings>({
    Context,
    storageManager,
    defaultSettings,
    children,
}: SettingsProviderProps<T>): JSX.Element {
    const [currentSettings, setCurrentSettings] = useState<T>(defaultSettings);
    const [userInfos, setUserInfos] = useState<UserInfos | null>(null);

    const storageKey = userInfos && `settings-o${userInfos.organizationId}-u${userInfos.userId}`;

    const clearSettings = useCallback((): void => {
        storageManager.removeItem(storageKey);
        storageManager.removeItem(DISPLAY_DEMO);
    }, [storageManager, storageKey]);

    const restoreSettings = useCallback((): T | null => {
        let settings = null;
        const validKey = Object.keys(storageManager).includes(storageKey) ? storageKey : "settings";

        try {
            const storedData: string | null = storageManager.getItem(validKey);

            if (storedData) {
                settings = JSON.parse(storedData);

                // Archive old settings to never reuse it again
                if (validKey === "settings") {
                    storageManager.setItem("settings-old", storedData);
                    storageManager.setItem(storageKey, storedData);
                    storageManager.removeItem("settings");
                }
                // TODO: Remove this migration after the 30th of June
                // Migrate margin-rate to roas in visibleKpis
                if (
                    settings &&
                    typeof settings === "object" &&
                    "welcomePagePreferences" in settings &&
                    "visibleKpis" in settings.welcomePagePreferences
                ) {
                    const visibleKpis = settings.welcomePagePreferences.visibleKpis;
                    if (Array.isArray(visibleKpis)) {
                        settings.welcomePagePreferences.visibleKpis = visibleKpis.map(
                            (kpi: string) => (kpi === "margin-rate" ? "roas" : kpi)
                        );
                    }
                }
            }
        } catch (err) {
            console.error(err);
            // If stored data is not a stringified JSON this will fail,
            // that's why we catch the error
        }

        return settings;
    }, [storageManager, storageKey]);

    const storeSettings = useCallback(
        (settings: T): void => {
            storageManager.setItem(storageKey, JSON.stringify(settings));
        },
        [storageManager, storageKey]
    );

    const handleClearSettings = useCallback((): void => {
        setCurrentSettings(defaultSettings);
        clearSettings();
    }, [clearSettings, defaultSettings]);

    const handleSaveSettings = useCallback(
        (update: T): void => {
            setCurrentSettings((settings) => {
                const mergedSettings = { ...settings, ...(update ?? {}) };

                storeSettings(mergedSettings);

                return mergedSettings;
            });
        },
        [storeSettings]
    );

    useEffect(() => {
        // When logging out we should not let other user settings
        if (!storageKey) {
            setCurrentSettings(defaultSettings);
        } else {
            const restoredSettings = restoreSettings();
            setCurrentSettings(restoredSettings ?? defaultSettings);
        }
    }, [defaultSettings, restoreSettings, storageKey]);

    return (
        <Context.Provider
            value={{
                settings: currentSettings,
                clearSettings: handleClearSettings,
                saveSettings: handleSaveSettings,
                setUserInfos,
            }}
        >
            {children}
        </Context.Provider>
    );
}
